import { useEffect, useReducer } from "react";
import { useI18n } from "@/i18n";
import { translateBatch } from "@/lib/api";

// Read-side translation of user-entered text (notes, scopes, task lines).
// Requests from every mounted <Tx> are pooled into one batch call per tick.
const cache = new Map();
const listeners = new Set();
let pending = new Set();
let timer = null;

async function flush(lang) {
  timer = null;
  const items = [...pending];
  pending = new Set();
  if (!items.length) return;
  for (let i = 0; i < items.length; i += 60) {
    const chunk = items.slice(i, i + 60);
    try {
      const r = await translateBatch(chunk, lang);
      chunk.forEach((s, j) => cache.set(`${lang}|${s}`, r.data?.translations?.[j] || s));
    } catch {
      chunk.forEach((s) => cache.set(`${lang}|${s}`, s));
    }
  }
  listeners.forEach((fn) => fn());
}

export function useTx(text, original, originalLang) {
  const { lang } = useI18n();
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => { listeners.add(bump); return () => listeners.delete(bump); }, []);
  if (!text || typeof text !== "string" || lang === "en") return text;
  if (original && originalLang === lang) return original;
  const k = `${lang}|${text}`;
  if (cache.has(k)) return cache.get(k);
  pending.add(text);
  if (!timer) timer = setTimeout(() => flush(lang), 60);
  return text;
}

export function Tx({ text, original, originalLang }) {
  const out = useTx(text, original, originalLang);
  return out ?? null;
}

// For list rows: <Tx {...txNote(n)} />
export const txNote = (n) => ({ text: n?.text ?? n?.note ?? "", original: n?.text_original, originalLang: n?.text_lang });
